import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, type ApolloError } from '@apollo/client';
import { RESPOND_TO_APPROVAL_MUTATION, SEND_CHAT_MESSAGE_MUTATION } from '../graphql/mutations';
import { CHAT_SESSION_QUERY } from '../graphql/queries';
import {
  openAgentStream,
  type AgentCitation,
  type AgentProposal,
  type AgentStreamFailure,
} from '../lib/agentStream';

/**
 * The chat turn state machine — one hook instance per open session (hosts
 * remount it per session). Owns the send/approve mutations, the SSE
 * consumption (fresh runId per turn, fresh AbortController per open, abort on
 * unmount), and the transcript: server-loaded turns for an existing session,
 * optimistically appended turns for live ones.
 *
 * Sessions are durable server-side; this hook persists nothing itself. On
 * mount with a `sessionId` it loads the render log via `chatSession` and
 * seeds the local transcript ONCE (sending is disabled until that load
 * lands, so seeding can never clobber an optimistic turn).
 */

/** One proposal as rendered (stream shape + query outcome fields). */
export interface ChatProposalView extends AgentProposal {
  ok?: boolean | null;
  version?: number | null;
  error?: string | null;
}

/** One transcript entry. */
export type ChatViewTurn =
  | { role: 'user'; text: string }
  | { role: 'assistant'; text: string; citations: AgentCitation[] }
  | {
      role: 'proposal';
      text: string;
      proposals: ChatProposalView[];
      resolution?: 'approved' | 'rejected' | 'superseded' | null;
    };

export type ChatPhase = 'idle' | 'submitting' | 'streaming' | 'deciding' | 'error';

export const MUTATION_FAILED_COPY =
  'Something went wrong sending this. Your text is still here — try again.';
export const SESSION_EXPIRED_COPY = 'Your session has expired. Please sign in again.';
export const RUN_GONE_COPY = 'That request is no longer available. Send it again to retry.';
export const DID_NOT_FINISH_COPY = "This didn't finish. Your text is still here — try again.";
export const SESSION_BUSY_COPY = "I'm still working on this conversation — give it a moment.";
export const APPROVAL_GONE_COPY =
  'This proposal is no longer pending — it may have been decided elsewhere.';

/** Single-status-line labels for tool_step events; never an accumulating checklist. */
const TOOL_STEP_LABELS: Record<string, string> = {
  read: 'Reading your notes…',
  search: 'Searching your notes…',
  list_docs: 'Looking through your documents…',
};

/**
 * Maps a stream failure to display copy: a calm lead `message`, plus an
 * optional `detail` line for terminal stream errors (`kind: 'event'`).
 */
function failureCopy(failure: AgentStreamFailure): { message: string; detail?: string } {
  if (failure.kind === 'http') {
    if (failure.status === 401) return { message: SESSION_EXPIRED_COPY };
    if (failure.status === 404 || failure.status === 409) return { message: RUN_GONE_COPY };
    return { message: DID_NOT_FINISH_COPY };
  }
  if (failure.kind === 'event' && failure.message) {
    return {
      message: DID_NOT_FINISH_COPY,
      detail: failure.code ? `${failure.code}: ${failure.message}` : failure.message,
    };
  }
  return { message: DID_NOT_FINISH_COPY };
}

interface ServerTurn {
  role: 'user' | 'assistant' | 'proposal';
  text: string | null;
  citations: AgentCitation[] | null;
  proposals: Array<{
    id: string;
    tool: 'write_doc' | 'append_to_doc';
    path: string;
    content: string;
    treeLabel: string;
    spaceId: string | null;
    ok: boolean | null;
    version: number | null;
    error: string | null;
  }> | null;
  resolution: 'approved' | 'rejected' | 'superseded' | null;
}

interface ChatSessionData {
  chatSession: {
    id: string;
    title: string;
    state: 'idle' | 'processing' | 'awaiting_approval';
    turns: ServerTurn[];
  } | null;
}

function mapServerTurns(turns: ServerTurn[]): ChatViewTurn[] {
  return turns.map((turn): ChatViewTurn => {
    if (turn.role === 'user') return { role: 'user', text: turn.text ?? '' };
    if (turn.role === 'assistant') {
      return { role: 'assistant', text: turn.text ?? '', citations: turn.citations ?? [] };
    }
    return {
      role: 'proposal',
      text: turn.text ?? '',
      proposals: (turn.proposals ?? []).map((p) => ({ ...p })),
      resolution: turn.resolution,
    };
  });
}

export interface UseChatSessionOptions {
  /** Existing session to load and continue; absent = a fresh session on first send. */
  sessionId?: string;
  /** Fires when the first send creates a session (hosts navigate / remember it). */
  onSessionCreated?: (sessionId: string) => void;
}

export function useChatSession({ sessionId, onSessionCreated }: UseChatSessionOptions) {
  const [turns, setTurns] = useState<ChatViewTurn[]>([]);
  const [phase, setPhase] = useState<ChatPhase>('idle');
  const [statusLabel, setStatusLabel] = useState('');
  const [streamed, setStreamed] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const sessionIdRef = useRef<string | undefined>(sessionId);
  const seededRef = useRef(false);

  // Hosts remount per session (keyed), but keep the ref honest regardless.
  useEffect(() => {
    if (sessionId) sessionIdRef.current = sessionId;
  }, [sessionId]);

  const [sendChatMessage] = useMutation(SEND_CHAT_MESSAGE_MUTATION);
  const [respondToApproval] = useMutation(RESPOND_TO_APPROVAL_MUTATION);

  const {
    data,
    loading: loadingSession,
    error: loadError,
  } = useQuery<ChatSessionData>(CHAT_SESSION_QUERY, {
    variables: { id: sessionId ?? '' },
    skip: !sessionId,
    fetchPolicy: 'network-only',
  });

  // Seed the transcript once from the server render log. Sending is disabled
  // until the load resolves, so this can never overwrite an optimistic turn.
  useEffect(() => {
    if (!sessionId || seededRef.current || !data?.chatSession) return;
    seededRef.current = true;
    setTurns(mapServerTurns(data.chatSession.turns));
  }, [sessionId, data]);

  // Abort an in-flight stream on unmount; the session stays consistent
  // server-side (a dropped turn is released by the route's failTurn).
  useEffect(() => () => abortRef.current?.abort(), []);

  const sessionMissing = Boolean(sessionId) && !loadingSession && !loadError && !data?.chatSession;

  /** The trailing proposal turn awaiting a decision (drives the approval card). */
  const pendingApproval =
    turns.length > 0 &&
    turns[turns.length - 1].role === 'proposal' &&
    !(turns[turns.length - 1] as { resolution?: string | null }).resolution
      ? (turns[turns.length - 1] as Extract<ChatViewTurn, { role: 'proposal' }>)
      : null;

  const openStream = (runId: string): void => {
    const controller = new AbortController();
    abortRef.current = controller;
    void openAgentStream(
      runId,
      {
        onToken: (tokenText) => {
          setStreamed((prev) => prev + tokenText);
        },
        onToolStep: (step) => {
          if (!step.ok) return;
          setStatusLabel(TOOL_STEP_LABELS[step.tool] ?? 'Working…');
        },
        onDone: (payload) => {
          setTurns((prev) => [
            ...prev,
            { role: 'assistant', text: payload.text, citations: payload.citations },
          ]);
          setStreamed('');
          setStatusLabel('');
          setPhase('idle');
        },
        onFailure: (failure) => {
          const { message, detail } = failureCopy(failure);
          setStreamed('');
          setStatusLabel('');
          setErrorMessage(message);
          setErrorDetail(detail ?? null);
          setPhase('error');
        },
        onApprovalRequired: (payload) => {
          setTurns((prev) => [
            ...prev,
            { role: 'proposal', text: payload.text, proposals: payload.proposals },
          ]);
          setStreamed('');
          setStatusLabel('');
          setPhase('idle');
        },
      },
      { signal: controller.signal }
    );
  };

  const send = async (text: string): Promise<boolean> => {
    const value = text.trim();
    if (!value || phase === 'submitting' || phase === 'streaming' || phase === 'deciding') {
      return false;
    }
    if (sessionId && loadingSession) return false;

    abortRef.current?.abort();
    setErrorMessage(null);
    setErrorDetail(null);
    setStreamed('');
    setPhase('submitting');
    setStatusLabel('Sending…');
    // A send while a proposal is pending supersedes it (mirrors the server).
    setTurns((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last && last.role === 'proposal' && !last.resolution) {
        next[next.length - 1] = { ...last, resolution: 'superseded' };
      }
      return [...next, { role: 'user', text: value }];
    });

    let runId: string | undefined;
    try {
      const { data: result } = await sendChatMessage({
        variables: {
          text: value,
          ...(sessionIdRef.current ? { sessionId: sessionIdRef.current } : {}),
        },
      });
      runId = result?.sendChatMessage?.runId;
      const newSessionId: string | undefined = result?.sendChatMessage?.sessionId;
      if (!runId || !newSessionId) throw new Error('No ticket returned');
      if (!sessionIdRef.current) {
        sessionIdRef.current = newSessionId;
        onSessionCreated?.(newSessionId);
      }
    } catch (err) {
      const code = (err as ApolloError)?.graphQLErrors?.[0]?.extensions?.code;
      setErrorMessage(code === 'BAD_USER_INPUT' ? SESSION_BUSY_COPY : MUTATION_FAILED_COPY);
      // Roll the optimistic user turn back so the transcript matches the server.
      setTurns((prev) => prev.slice(0, -1));
      setPhase('error');
      setStatusLabel('');
      return false;
    }

    setPhase('streaming');
    setStatusLabel('Thinking…');
    openStream(runId);
    return true;
  };

  const respond = async (approve: boolean): Promise<void> => {
    if (!pendingApproval || phase === 'deciding' || phase === 'streaming') return;
    const targetSessionId = sessionIdRef.current;
    if (!targetSessionId) return;

    setPhase('deciding');
    setErrorMessage(null);
    setErrorDetail(null);

    let runId: string | undefined;
    try {
      const { data: result } = await respondToApproval({
        variables: { sessionId: targetSessionId, approve },
      });
      runId = result?.respondToApproval?.runId;
      if (!runId) throw new Error('No ticket returned');
    } catch (err) {
      const code = (err as ApolloError)?.graphQLErrors?.[0]?.extensions?.code;
      setErrorMessage(code === 'BAD_USER_INPUT' ? APPROVAL_GONE_COPY : MUTATION_FAILED_COPY);
      setPhase('error');
      return;
    }

    // Mark the proposal turn resolved (per-proposal outcomes stay whatever the
    // approval card showed; the session query has the authoritative record).
    setTurns((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last && last.role === 'proposal' && !last.resolution) {
        next[next.length - 1] = {
          ...last,
          resolution: approve ? 'approved' : 'rejected',
          proposals: last.proposals.map((p) => (approve ? { ...p, ok: p.ok ?? true } : p)),
        };
      }
      return next;
    });
    setPhase('streaming');
    setStatusLabel(approve ? 'Saving…' : 'Okay…');
    setStreamed('');
    openStream(runId);
  };

  const busy = phase === 'submitting' || phase === 'streaming' || phase === 'deciding';

  return {
    turns,
    phase,
    busy,
    statusLabel,
    streamed,
    errorMessage,
    errorDetail,
    pendingApproval,
    loadingSession: Boolean(sessionId) && loadingSession,
    loadError: Boolean(loadError),
    sessionMissing,
    sessionTitle: data?.chatSession?.title ?? null,
    send,
    respond,
  };
}
